import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Sparkles, ArrowRight, Star, Tag, PartyPopper } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { publicServicesApi, serviceCategoriesApi } from "@/lib/api";
import { priceLabel, serviceCover, BOOKING_TYPES } from "@/lib/serviceConstants";

function ServiceCard({ svc, idx }) {
  const cover = serviceCover(svc);
  const bt = BOOKING_TYPES.find((b) => b.value === svc.booking_type);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (idx % 3) * 0.07 }}
    >
      <Link to={`/services/${svc.slug}`} data-testid={`service-card-${svc.id}`} className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
        <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
          {cover ? <img src={cover} alt={svc.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="flex h-full items-center justify-center"><PartyPopper className="h-10 w-10 text-muted-foreground" /></div>}
          {svc.featured && <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-full bg-gold px-3 py-1 text-xs font-semibold text-primary"><Star className="h-3 w-3" /> Featured</span>}
          {bt && <span className="absolute right-4 top-4 rounded-full bg-black/55 px-3 py-1 text-xs font-medium text-white backdrop-blur">{bt.cta}</span>}
        </div>
        <div className="flex flex-1 flex-col p-6">
          <h3 className="font-serif text-xl font-semibold leading-snug">{svc.name}</h3>
          {svc.short_description && <p className="mt-2 line-clamp-2 flex-1 text-sm text-muted-foreground">{svc.short_description}</p>}
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm font-semibold text-gold">{priceLabel(svc)}</span>
            <span className="inline-flex items-center gap-1 text-sm font-medium text-foreground/70 transition-transform group-hover:translate-x-1">View <ArrowRight className="h-4 w-4" /></span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function Services() {
  const [services, setServices] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (active) params.category = active;
      if (q) params.q = q;
      setServices(await publicServicesApi.list(params));
    } finally {
      setLoading(false);
    }
  }, [active, q]);

  useEffect(() => { serviceCategoriesApi.listPublic().then((c) => setCats(c.filter((x) => !x.parent_id))).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <section className="relative overflow-hidden border-b border-border bg-primary px-5 py-20 text-center sm:px-8 sm:py-28">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #cfa53b 0, transparent 40%), radial-gradient(circle at 80% 60%, #cfa53b 0, transparent 35%)" }} />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative mx-auto max-w-3xl">
          <p className="eyebrow mb-3 text-gold">Event Services</p>
          <h1 className="font-serif text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl">Everything your event needs</h1>
          <p className="mx-auto mt-5 max-w-xl text-base text-white/70">From décor and catering to photography, entertainment and production — explore our curated services and bring your celebration to life.</p>
        </motion.div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-12 sm:px-8 sm:py-16">
        <div className="mb-8 flex flex-col gap-4">
          <div className="relative mx-auto w-full max-w-xl">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="services-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search services…" className="w-full rounded-full border border-input bg-white py-3 pl-11 pr-4 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/20" />
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <button onClick={() => setActive("")} data-testid="cat-chip-all" className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${!active ? "bg-primary text-primary-foreground" : "border border-border hover:bg-secondary"}`}>All</button>
            {cats.map((c) => (
              <button key={c.id} onClick={() => setActive(c.slug)} data-testid={`cat-chip-${c.slug}`} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${active === c.slug ? "bg-primary text-primary-foreground" : "border border-border hover:bg-secondary"}`}>{c.name}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-80 animate-pulse rounded-3xl bg-secondary/60" />)}</div>
        ) : services.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border bg-card p-16 text-center" data-testid="services-empty">
            <Sparkles className="mx-auto h-10 w-10 text-gold" />
            <h3 className="mt-4 font-serif text-2xl font-medium">No services found</h3>
            <p className="mt-2 text-sm text-muted-foreground">Try a different category or search, or tell us what you need.</p>
            <Link to="/#plan" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">Plan your event <ArrowRight className="h-4 w-4" /></Link>
          </div>
        ) : (
          <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
            {services.map((s, i) => <ServiceCard key={s.id} svc={s} idx={i} />)}
          </div>
        )}
      </section>

      <section className="border-t border-border bg-secondary/40 px-5 py-16 text-center sm:px-8">
        <Tag className="mx-auto h-7 w-7 text-gold" />
        <h2 className="mt-4 font-serif text-3xl font-semibold">Can't find exactly what you need?</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">Tell us about your event and we'll craft a custom package tailored to you.</p>
        <Link to="/#plan" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground transition-all hover:shadow-lg">Request a custom quote <ArrowRight className="h-4 w-4" /></Link>
      </section>
    </div>
  );
}
